import { cn } from '../lib/cn';
import { useBrand } from '../brand/BrandProvider';

/**
 * The brand mark, used by every auth page (login, signup, forgot/reset password, invite,
 * bootstrap, become-a-reseller) and anywhere else that needs a small square logo.
 *
 * It renders the host's UPLOADED logo when there is one, and falls back to the built-in
 * geometric "P" only when there isn't.
 *
 * That fallback used to be the ONLY thing it rendered. Every auth page called `<Logo />`
 * beside `{brand.name}`, so the NAME was white-labelled while the MARK never was — a
 * customer opening ogrogoti.com/login saw Possignin's glyph next to the word "Ogrogoti",
 * which is exactly the leak Decision #21 / SECURITY_COMPLIANCE_CHECKLIST §22 exist to
 * prevent. It also meant the logos an admin had uploaded, validated and could see in the
 * admin UI were rendered by nothing on the one page every customer passes through.
 *
 * Light/dark follows the same rule as the marketing marks (BrandMarks.tsx's LogoImage):
 * swap on the `dark` class so there is no first-paint flash, and when no dark variant is
 * uploaded use the light one for both — a reseller with a single mid-tone mark is readable
 * either way, and a wrong-but-present logo beats a missing one.
 */
export function Logo({ className }: { className?: string }) {
  const brand = useBrand();
  const box = cn('w-7 h-7 rounded-lg grid place-items-center', className);

  if (brand.logoUrl) {
    // object-contain + a neutral box: an uploaded mark may be any aspect ratio, and cropping
    // someone's logo is worse than letterboxing it.
    const img = 'w-full h-full object-contain rounded-lg';
    return brand.logoDarkUrl ? (
      <span className={box}>
        <img src={brand.logoUrl} alt="" className={`${img} dark:hidden`} />
        <img src={brand.logoDarkUrl} alt="" className={`${img} hidden dark:block`} />
      </span>
    ) : (
      <span className={box}>
        <img src={brand.logoUrl} alt="" className={img} />
      </span>
    );
  }

  return (
    <span className={cn(box, 'bg-zinc-950 dark:bg-zinc-50')} aria-hidden="true">
      <svg viewBox="0 0 24 24" className="w-4 h-4 text-white dark:text-zinc-950" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 4h9a5 5 0 0 1 0 10H8v6" />
      </svg>
    </span>
  );
}
